import { useTranslation } from 'react-i18next';
import { useMemo, useCallback } from 'react';

// Map i18next language codes to Intl locale codes
const localeMap: Record<string, string> = {
  'en': 'en-US',
  'pt-BR': 'pt-BR',
  'es': 'es-ES',
};

/**
 * Format numbers, currency, percent, and durations using i18n locale.
 */
export function useNumberFormat() {
  const { i18n } = useTranslation();
  
  const locale = useMemo(() => {
    return localeMap[i18n.language] || 'en-US';
  }, [i18n.language]);

  // Format integer numbers with locale-specific thousand separators
  const formatNumber = useCallback((value: number | undefined | null): string => {
    if (value === undefined || value === null) return '0';
    return value.toLocaleString(locale);
  }, [locale]);

  // Format decimal numbers with specified decimal places
  const formatDecimal = useCallback((
    value: number | undefined | null, 
    decimals: number = 2
  ): string => {
    if (value === undefined || value === null) return '0';
    return value.toLocaleString(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }, [locale]);

  // Format percentage with 2 decimal places (always)
  // isAverage: if true, prepend ~ prefix
  const formatPercent = useCallback((
    value: number | undefined | null,
    isAverage: boolean = false
  ): string => {
    if (value === undefined || value === null) return isAverage ? '~0.00%' : '0.00%';
    const formatted = value.toFixed(2);
    return isAverage ? `~${formatted}%` : `${formatted}%`;
  }, []);

  // Format currency with locale-specific formatting
  const formatCurrency = useCallback((
    value: number | undefined | null,
    currency: string = 'USD',
    decimals: number = 4
  ): string => {
    const safeValue = Number(value === undefined || value === null ? 0 : value);
    try {
      return safeValue.toLocaleString(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
    } catch {
      return `${currency} ${safeValue.toLocaleString(locale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })}`;
    }
  }, [locale]);

  // Format duration in mm:ss format (minutes can exceed 59; optionally with milliseconds)
  // isAverage: if true, prepend ~ prefix
  const formatDuration = useCallback((
    seconds: number | undefined | null,
    isAverage: boolean = false,
    includeMilliseconds: boolean = false
  ): string => {
    if (seconds === undefined || seconds === null || seconds === 0) {
      const base = includeMilliseconds ? '0:00.000' : '0:00';
      return isAverage ? `~${base}` : base;
    }
    const totalSeconds = Math.max(0, seconds);
    const totalMinutes = Math.floor(totalSeconds / 60);
    const wholeSeconds = Math.floor(totalSeconds % 60);
    const milliseconds = Math.round((totalSeconds - Math.floor(totalSeconds)) * 1000);
    const safeMilliseconds = milliseconds === 1000 ? 0 : milliseconds;
    const safeSeconds = milliseconds === 1000 ? (wholeSeconds + 1) : wholeSeconds;
    const normalizedSeconds = safeSeconds >= 60 ? 0 : safeSeconds;
    const rolloverMinutes = safeSeconds >= 60 ? 1 : 0;
    const normalizedMinutes = totalMinutes + rolloverMinutes;
    const secondsLabel = includeMilliseconds
      ? `${normalizedSeconds.toString().padStart(2, '0')}.${safeMilliseconds.toString().padStart(3, '0')}`
      : normalizedSeconds.toString().padStart(2, '0');
    const formatted = `${normalizedMinutes}:${secondsLabel}`;
    return isAverage ? `~${formatted}` : formatted;
  }, []);

  const formatLatency = useCallback((
    seconds: number | undefined | null,
    isAverage: boolean = false
  ): string => {
    const includeMilliseconds = true;
    return formatDuration(seconds, isAverage, includeMilliseconds);
  }, [formatDuration]);
  
  // Format any value with average prefix if needed
  const formatAverage = useCallback(<T extends string | number>(
    value: T,
    formatter?: (v: T) => string
  ): string => {
    if (formatter) {
      return `~${formatter(value)}`;
    }
    return `~${value}`;
  }, []);

  return {
    locale,
    formatNumber,
    formatDecimal,
    formatPercent,
    formatCurrency,
    formatDuration,
    formatLatency,
    formatAverage,
  };
}
